// v49.57 — HelpWidget.
// Floating help button (bottom-right) that opens a slide-up panel
// for in-app feedback. Reports land in the client_errors table
// (v49.52) tagged source=user_report, so they sit alongside the
// auto-captured runtime exceptions in one queryable surface.
//
// Mobile: positioned above the 64px bottom-nav so it doesn't
// collide. Desktop: 24px from the corner.

import { useEffect, useRef, useState } from "react";
import { useLocation } from "react-router";
import { reportUserFeedback, type FeedbackCategory } from "@/lib/errorReporter";
import { APP_VERSION } from "@/lib/version";
import { useBreakpoint } from "@/lib/useBreakpoint";
import { useToast } from "@/components/Toast";
import { Button } from "@/components/Button";

const CATEGORIES: { id: FeedbackCategory; label: string; hint: string; tone: string }[] = [
  { id: "bug",     label: "Something's broken",  hint: "Crash, blank screen, wrong data, button doesn't fire.",         tone: "var(--color-lc-red)" },
  { id: "feature", label: "Feature suggestion",  hint: "Something you wish the platform did but doesn't yet.",          tone: "var(--color-lc-blue)" },
  { id: "other",   label: "Other feedback",      hint: "Confusing copy, a typo, a question, or anything else.",         tone: "#7C3AED" },
];

/* Per-session rate limit. Stops accidental double-submits and
   genuine spam. */
const MAX_PER_MIN = 5;
const _recent: number[] = [];
function withinRateLimit(): boolean {
  const now = Date.now();
  while (_recent.length > 0 && now - _recent[0] > 60_000) _recent.shift();
  if (_recent.length >= MAX_PER_MIN) return false;
  _recent.push(now);
  return true;
}

export function HelpWidget() {
  const { pathname } = useLocation();
  const bp           = useBreakpoint();
  const toast        = useToast();
  const [open, setOpen]         = useState(false);
  const [category, setCategory] = useState<FeedbackCategory>("bug");
  const [message, setMessage]   = useState("");
  const [busy, setBusy]         = useState(false);
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  /* Close on Escape. */
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => { if (e.key === "Escape") setOpen(false); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open]);

  /* Focus textarea on open. */
  useEffect(() => {
    if (!open) return;
    const t = setTimeout(() => textareaRef.current?.focus(), 60);
    return () => clearTimeout(t);
  }, [open]);

  /* Reset on close. */
  useEffect(() => {
    if (open) return;
    setMessage("");
    setCategory("bug");
  }, [open]);

  const submit = async () => {
    const trimmed = message.trim();
    if (trimmed.length < 8) return;
    if (!withinRateLimit()) {
      toast({ kind: "warn", msg: "Too many reports", subtitle: "Please wait a minute before sending another." });
      return;
    }
    setBusy(true);
    /* reportUserFeedback is fire-and-forget — no await needed but we
       briefly disable the form so the user sees feedback. */
    reportUserFeedback({ category, message: trimmed });
    /* Give the dispatch a beat so the spinner has visible weight. */
    setTimeout(() => {
      setBusy(false);
      toast({ kind: "success", msg: "Thanks — feedback sent", subtitle: "We've logged it for the team. We'll be in touch if we need detail." });
      setOpen(false);
    }, 400);
  };

  const isMobile = bp === "mobile";
  /* Position the button. Mobile: above the 64px bottom nav + safe-
     area inset. Desktop: standard 24px from the corner. */
  const buttonStyle = isMobile
    ? { bottom: "calc(80px + env(safe-area-inset-bottom, 0px))", right: "16px" }
    : { bottom: "24px", right: "24px" };

  return (
    <>
      {/* Floating launcher */}
      {!open && (
        <button
          type="button"
          onClick={() => setOpen(true)}
          aria-label="Help and feedback"
          className="fixed z-30 w-12 h-12 rounded-full text-white flex items-center justify-center shadow-xl transition-transform hover:scale-105 active:scale-95"
          style={{
            ...buttonStyle,
            background: "linear-gradient(135deg, var(--color-lc-blue-dark), var(--color-lc-blue))",
            boxShadow: "0 8px 24px rgba(37,99,235,.35), 0 2px 4px rgba(37,99,235,.20), inset 0 1px 0 rgba(255,255,255,.18)",
            WebkitTapHighlightColor: "transparent",
          }}
        >
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
            <circle cx="12" cy="12" r="10" />
            <path d="M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3" />
            <line x1="12" y1="17" x2="12.01" y2="17" />
          </svg>
        </button>
      )}

      {/* Backdrop + panel */}
      {open && (
        <div
          className="fixed inset-0 z-40 flex items-end md:items-center justify-center md:justify-end p-0 md:p-6"
          style={{ background: "rgba(8,17,33,.45)", backdropFilter: "blur(4px)" }}
          onClick={() => !busy && setOpen(false)}
        >
          <div
            className="w-full md:w-[420px] bg-white rounded-t-2xl md:rounded-lc-lg shadow-2xl overflow-hidden lc-safe-pb"
            onClick={e => e.stopPropagation()}
            role="dialog"
            aria-modal="true"
            aria-label="Send feedback"
          >
            {/* Grabber on mobile */}
            <div className="md:hidden pt-2 pb-1 flex justify-center">
              <div className="w-10 h-1 rounded-full bg-lc-line" />
            </div>
            <header
              className="px-5 py-3.5 text-white"
              style={{ background: "linear-gradient(135deg, var(--color-lc-navy), var(--color-lc-blue-dark))" }}
            >
              <div className="flex items-center justify-between gap-3">
                <div>
                  <div className="text-[11px] font-extrabold uppercase tracking-widest text-blue-300">Help</div>
                  <div className="text-base font-extrabold tracking-tight mt-0.5">Send feedback</div>
                </div>
                <button
                  type="button"
                  onClick={() => setOpen(false)}
                  disabled={busy}
                  aria-label="Close"
                  className="w-7 h-7 rounded-full bg-white/15 hover:bg-white/25 flex items-center justify-center transition-colors"
                >
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                    <line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" />
                  </svg>
                </button>
              </div>
            </header>

            <div className="px-5 py-4 flex flex-col gap-3">
              <div>
                <div className="text-[10px] font-extrabold uppercase tracking-widest text-lc-mute">What's it about?</div>
                <div className="mt-2 flex flex-col gap-1.5">
                  {CATEGORIES.map(c => {
                    const active = category === c.id;
                    return (
                      <button
                        key={c.id}
                        type="button"
                        onClick={() => setCategory(c.id)}
                        className={`flex items-center gap-3 px-3 py-2 rounded-lc-md border-2 text-left transition-colors ${active ? "bg-lc-blue-light/40" : "bg-white hover:bg-lc-bg-2/40"}`}
                        style={{ borderColor: active ? c.tone : "var(--color-lc-line)" }}
                      >
                        <span className="w-2 h-2 rounded-full shrink-0" style={{ background: c.tone }} />
                        <span className="flex-1 min-w-0">
                          <span className="block text-sm font-extrabold text-lc-ink">{c.label}</span>
                          <span className="block text-[12px] text-lc-mute leading-snug">{c.hint}</span>
                        </span>
                      </button>
                    );
                  })}
                </div>
              </div>

              <div>
                <div className="text-[10px] font-extrabold uppercase tracking-widest text-lc-mute">Tell us more</div>
                <textarea
                  ref={textareaRef}
                  value={message}
                  onChange={e => setMessage(e.target.value)}
                  rows={4}
                  maxLength={2000}
                  placeholder={
                    category === "bug"     ? "What were you trying to do? What happened instead?"
                    : category === "feature" ? "What would you like the platform to do?"
                    :                          "Anything you want to tell us…"
                  }
                  className="mt-1.5 w-full px-3 py-2 rounded-lc-sm border border-lc-line text-sm focus:outline-none focus:border-lc-blue resize-y"
                />
                <div className="text-[10px] text-lc-faint mt-1 flex justify-between">
                  <span>{message.length}/2000 — min 8 chars</span>
                  <span>{APP_VERSION} · {pathname}</span>
                </div>
              </div>

              <div className="rounded-lc-md bg-lc-bg-soft border border-lc-line px-3 py-2.5">
                <div className="text-[11px] text-lc-mute leading-relaxed">
                  Submissions are logged to the LabourCore platform — we capture your URL, app version, and browser to help us track it down.
                  For urgent issues you can also email{" "}
                  <a href="mailto:sam@labourcore.co.uk" className="text-lc-blue-dark hover:underline font-bold">
                    sam@labourcore.co.uk
                  </a>.
                </div>
              </div>
            </div>

            <footer className="px-5 py-3 border-t border-lc-line flex items-center justify-end gap-2 bg-white">
              <Button type="button" variant="ghost" onClick={() => setOpen(false)} disabled={busy}>
                Cancel
              </Button>
              <Button type="button" variant="primary" disabled={busy || message.trim().length < 8} onClick={submit}>
                {busy ? "Sending…" : "Send feedback"}
              </Button>
            </footer>
          </div>
        </div>
      )}
    </>
  );
}
